import { useCallback, useEffect, useState, useRef } from 'react';
import axios from 'Axios';
import Select from 'react-select';
import { rgba } from 'polished';
import { useDispatch } from 'react-redux';
import { Id, Pencil, Plus } from 'tabler-icons-react';
import { api, clx, toastify } from 'Utils';
import { selectStyles } from 'Styles';
import { setUser } from 'Actions';
import {
	ContentBox,
	SubmitButton,
	Button,
	RadioGroup,
	RadioItem
} from 'Elements';
import styles from 'partials/dashboard/user-form.module.scss';
import buttonStyles from 'partials/button.module.scss';

export const UserInfoForm = ({ user }) => {
	const fileInput = useRef(null);
	const dispatch = useDispatch();
	const [state, setState] = useState({
		fields: {
			image: '',
			avatar: null,
			username: '',
			name: '',
			mobile: '',
			email: '',
			code_melli: '',
			nationality: '',
			job: '',
			gender: null,
			education: null,
			birth_date: '',
			social_networks: []
		},
		degrees: [
			{ value: '1', label: 'حوزوی' },
			{ value: '2', label: 'دیپلم' },
			{ value: '3', label: 'فوق دیپلم' },
			{ value: '4', label: 'لیسانس' },
			{ value: '5', label: 'فوق لیسانس' },
			{ value: '6', label: 'دکترا' }
		],
		isSubmiting: false
	});

	const customSelect = {
		...selectStyles,
		control: () => ({
			...selectStyles.control(),
			borderRadius: 10,
			borderColor: '#fff',
			// boxShadow:
			// 	state.isFocused &&
			// 	`0 1px 5px ${rgba(rgba('#203696', 0.2), 0.25)} inset`,
			boxShadow: `0 3px 6px ${rgba('#203696', 0.2)}`,
			transition: 'all 0.3s'
		}),
		menu: () => ({
			...selectStyles.menu(),
			borderRadius: 10,
			border: `1px solid ${rgba('#c9cdd9', 0.5)}`
		}),
		singleValue: () => ({
			...selectStyles.singleValue(),
			fontSize: '14px',
			color: ' #001727'
		}),
		option: () => ({
			...selectStyles.option(),
			':not(:last-child)': {
				borderBottom: `1px solid ${rgba('#c9cdd9', 0.5)}`
			}
		})
	};

	const addSocial = () => {

	}

	const handleInput = (e) => {
		const target = e.target;
		const fields = state.fields;
		fields[target.name] = target.value;
		setState((prev) => ({
			...prev,
			fields
		}));
	};

	const handleSelect = (selected) => {
		const fields = state.fields;
		fields.education = selected;
		setState((prev) => ({
			...prev,
			fields
		}));
	};

	const getSelectOption = useCallback(
		(selected) => {
			const result = state.degrees.find(
				(degree) => degree.value === selected
			);
			return result;
		},
		[state.degrees]
	);

	const getGender = (gender) => {
		const type = {
			0: 'sir',
			1: 'madam'
		};
		return type[gender];
	};

	const getProfile = useCallback(() => {
		const userInfo = user;
		delete userInfo.id;
		setState((prev) => ({
			...prev,
			fields: {
				...userInfo,
				education: getSelectOption(userInfo?.education),
				gender: getGender(userInfo?.gender),
				image: userInfo?.avatar
			}
		}));
		dispatch(setUser(user));
	}, [user, getSelectOption, dispatch]);

	const handleUploadImage = (e) => {
		e.stopPropagation();
		e.preventDefault();

		const target = e.target;
		const file = target.files[0];

		const reader = new FileReader();
		reader.onloadend = () => {
			setState((prev) => ({
				...prev,
				fields: {
					...prev.fields,
					image: reader.result,
					avatar: file
				}
			}));
		};
		reader.readAsDataURL(file);
	};

	const onUploadImage = () => {
		fileInput.current.click();
	};

	const handleSubmit = (e) => {
		e.preventDefault();

		if (!state.fields.gender) {
			toastify('انتخاب جنسیت الزامی است');
		} else {
			setState((prev) => ({
				...prev,
				isSubmiting: true
			}));
			const formData = new FormData();
			for (const field in state.fields) {
				if (field === 'education') {
					formData.append(field, state.fields[field].value);
				} else {
					formData.append(field, state.fields[field] ?? '');
				}
			}
			axios.post(api.updateProfile, formData).then((res) => {
				setState((prev) => ({
					...prev,
					isSubmiting: false
				}));
				const data = res.data;
				if (data.success) {
					toastify('اطلاعات کاربری با موفقیت ویرایش شد', 'success');
				}
			});
		}
	};

	useEffect(() => {
		getProfile();
	}, [getProfile]);

	return (
		<ContentBox
			title="اطلاعات فردی"
			icon={Id}
			subtitle="در این قسمت لطفا اطلاعات خود را تکمیل کنید">
			<form className={styles.form} onSubmit={handleSubmit}>
				<div className={clx(styles.field, styles.field_center)}>
					<input
						type="file"
						ref={fileInput}
						className="dis-none"
						accept=".png, .jpg, .jpeg, .gif"
						onChange={handleUploadImage}
					/>
					<div
						role="button"
						tabIndex="-1"
						className={styles.imageBox}
						onClick={onUploadImage}
						onKeyPress={onUploadImage}>
						<img
							src={state.fields.image}
							className={styles.image}
							alt=""
						/>
						<span className={styles.imageEdit}>
							<Pencil size={22} />
						</span>
					</div>
				</div>
				<div className={styles.field}>
					<label htmlFor="username" className={styles.label}>
						نام کاربری
					</label>
					<input
						disabled
						type="text"
						id="username"
						name="username"
						className={styles.input}
						defaultValue={state.fields.username}
						onChange={handleInput}
					/>
				</div>
				<div className={styles.field}>
					<label htmlFor="name" className={styles.label}>
						نام و نام خانوادگی
						<span className={styles.required}>(الزامی)</span>
					</label>
					<input
						type="text"
						id="name"
						name="name"
						className={styles.input}
						defaultValue={state.fields.name}
						onChange={handleInput}
					/>
				</div>
				<div className={styles.field}>
					<label htmlFor="mobile" className={styles.label}>
						تلفن همراه
						<span className={styles.required}>(الزامی)</span>
					</label>
					<input
						type="tel"
						id="mobile"
						name="mobile"
						className={clx(styles.input, styles.input_ltr)}
						defaultValue={state.fields.mobile}
						onChange={handleInput}
					/>
				</div>
				<div className={styles.field}>
					<label htmlFor="email" className={styles.label}>
						پست الکترونیکی
					</label>
					<input
						type="email"
						id="email"
						name="email"
						className={clx(styles.input, styles.input_ltr)}
						defaultValue={state.fields.email}
						onChange={handleInput}
					/>
				</div>
				<div className={styles.field}>
					<label htmlFor="code_melli" className={styles.label}>
						کد ملی
					</label>
					<input
						type="text"
						inputMode="numeric"
						id="code_melli"
						name="code_melli"
						className={clx(styles.input, styles.input_ltr)}
						defaultValue={state.fields.code_melli}
						onChange={handleInput}
					/>
				</div>
				<div className={styles.field}>
					<label htmlFor="nationality" className={styles.label}>
						ملیت
					</label>
					<input
						type="text"
						id="nationality"
						name="nationality"
						className={styles.input}
						defaultValue={state.fields.nationality}
						onChange={handleInput}
					/>
				</div>
				<div className={styles.field}>
					<label htmlFor="gender" className={styles.label}>
						جنسیت
						<span className={styles.required}>(الزامی)</span>
					</label>
					<RadioGroup
						className="d-flex"
						name="gender"
						value={state.fields.gender}
						onChange={handleInput}>
						<RadioItem
							id="sir"
							value="sir"
							label="مرد"
							className={styles.radio}
						/>
						<RadioItem
							id="madam"
							value="madam"
							label="زن"
							className={styles.radio}
						/>
					</RadioGroup>
				</div>
				{/* <div className={styles.field}>
					<label htmlFor="birthdate" className={styles.label}>
						تاریخ تولد
					</label>
				</div> */}
				<div className={styles.field}>
					<label htmlFor="education" className={styles.label}>
						تحصیلات
					</label>
					<Select
						isRtl
						placeholder=""
						closeMenuOnScroll
						isSearchable={false}
						styles={customSelect}
						className={styles.select}
						value={state.fields.education}
						options={state.degrees}
						onChange={handleSelect}
					/>
				</div>
				<div className={styles.field}>
					<label htmlFor="job" className={styles.label}>
						شغل
					</label>
					<input
						type="text"
						id="job"
						name="job"
						className={styles.input}
						defaultValue={state.fields.job}
						onChange={handleInput}
					/>
				</div>
				<div className={styles.field}>
					<label htmlFor="social_networks" className={styles.label}>
						شبکه های اجتماعی
					</label>
					<div className={styles.fieldGroup}>
						<Select
							isRtl
							placeholder=""
							closeMenuOnScroll
							isSearchable={false}
							styles={customSelect}
							className={styles.selectSocial}
							value={state.fields.education}
							options={state.degrees}
							onChange={handleSelect}
						/>
						<input
							type="text"
							id="job"
							name="job"
							className={styles.input}
							defaultValue={state.fields.job}
							onChange={handleInput}
						/>
					</div>
						<Button className={styles.btnText} onClick={addSocial}>
							<Plus size={26} /> افزودن شبکه های اجتماعی
						</Button>
				</div>
				<SubmitButton
					className={[styles.submit, buttonStyles.btn].join(' ')}
					disabled={state.isSubmiting}
					title="ذخیره"
				/>
			</form>
		</ContentBox>
	);
};
